import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ArrowUpDown, TrendingUp, Star, Clock } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';
import type { TranslationKey } from '../i18n/index';

export type SortOption =
  | 'relevance'
  | 'price_asc'
  | 'price_desc'
  | 'rating'
  | 'newest';

interface SortDropdownProps {
  value: SortOption;
  onChange: (value: SortOption) => void;
}

const sortOptionDefs: Array<{ value: SortOption; labelKey: TranslationKey; icon: React.ReactNode }> = [
  { value: 'relevance', labelKey: 'sort.relevance', icon: <TrendingUp size={14} /> },
  { value: 'price_asc', labelKey: 'sort.priceAsc', icon: <ArrowUpDown size={14} /> },
  { value: 'price_desc', labelKey: 'sort.priceDesc', icon: <ArrowUpDown size={14} /> },
  { value: 'rating', labelKey: 'sort.rating', icon: <Star size={14} /> },
  { value: 'newest', labelKey: 'sort.newest', icon: <Clock size={14} /> },
];

export const SortDropdown: React.FC<SortDropdownProps> = React.memo(
  ({ value, onChange }) => {
    const { t } = useI18n();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = sortOptionDefs.find((opt) => opt.value === value) || sortOptionDefs[0];

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // composedPath() works whether mounted normally or inside a Shadow DOM
        // (where event.target is retargeted to the shadow host at document level)
        if (dropdownRef.current && !event.composedPath().includes(dropdownRef.current)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option: SortOption) => {
      onChange(option);
      setIsOpen(false);
    };

    return (
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-[2px] hover:border-gray-500 transition-colors"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {selectedOption.icon}
          {/* Single label (not the old dual show/hide spans, which rendered
              BOTH on embeds where the responsive classes misbehaved, widening
              the control until the filter button overflowed). Bounded width on
              mobile so a long sort label can't push siblings off-screen. */}
          <span className="truncate max-w-[7.5rem] sm:max-w-none">{t(selectedOption.labelKey)}</span>
          <ChevronDown
            size={14}
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div
            className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-[2px] shadow-[0_1px_5px_rgba(0,0,0,0.2)] z-50 py-1 animate-fade-in"
            role="listbox"
          >
            {sortOptionDefs.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                  option.value === value
                    ? 'text-black font-semibold'
                    : 'text-gray-700'
                }`}
                role="option"
                aria-selected={option.value === value}
              >
                {option.icon}
                {t(option.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

SortDropdown.displayName = 'SortDropdown';
